import { useEffect, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import locale from "@bonliva-crm/locale";
import { MultiSelect } from "@bonliva-crm/ui";
import { useUpdateEffect } from "@hooks";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { isFilterSelected } from "../utils/is-filter-selected";

type Option = { label: string; value: string };

export type TagsFilterProps = {
  getOptions: () => void;
  tags: string[];
  loading: boolean;
} & FilterWithFavouriteProps;

export const TagsFilter = ({ getOptions, tags, loading, ...props }: TagsFilterProps) => {
  const selectRef = useRef(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const title = props.label ?? locale.tags;

  useEffect(() => {
    if (expanded) {
      getOptions();
    }
  }, [expanded]);

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const clear = () => resetField(props.name);

  const onChange = (value: Option[]) => {
    setValue(props.name, value, { shouldDirty: true });
  };
  const onClear = () => {
    setValue(props.name, null, { shouldDirty: true });
  };

  const inputOptions = [
    { label: "Saknar tagg", value: null },
    ...tags.map((tag) => ({ label: tag, value: tag }))
  ];

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiSelect<Option>
          selectRef={selectRef}
          name={props.name}
          isMulti={true}
          isLoading={loading}
          options={inputOptions}
          value={field.value}
          showDropDownIndicator={true}
          maxMenuHeight={300}
          controlShouldRenderValue
          openMenuOnFocus
          menuPortalTarget={document.body}
          showBadge={false}
          hidePlaceholderOnFocus={false}
          placeholder={locale.SelectValue}
          optionType="checkbox"
          onChange={onChange}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            control: { showBorder: false },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
          closeMenuOnSelect={false}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <div style={{ minWidth: 140 }}>
      <MultiSelect<Option>
        name={props.name}
        hidePlaceholderOnFocus={false}
        hideSelectedOptions={false}
        showBadge={isFilterSelected(field?.value)}
        isMulti={true}
        onMenuOpen={() => getOptions()}
        isLoading={loading}
        options={inputOptions}
        value={field.value}
        showDropDownIndicator={true}
        isClearable={true}
        onClear={onClear}
        maxMenuHeight={300}
        controlShouldRenderValue={false}
        placeholder={title}
        onChange={onChange}
        optionType={"checkbox"}
        styleProps={{ menu: { showBorder: true }, control: { showBorder: true } }}
        closeMenuOnSelect={false}
      />
    </div>
  );
};
